import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { NgxMaskModule } from 'ngx-mask';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { OnlyDigitsDirective } from '../../../shared/directives/only-digits.directive';
import { DropdownComponent } from '../../../shared/dropdown/dropdown.component';
import { CustomCurrencyPipe } from '../../../shared/pipes/currency.pipe';
import { CharCounterComponent } from '../item-write/char-counter/char-counter.component';
import { ItemSimpleComponent } from '../item-simple/item-simple.component';
import { ItemTextModalComponent } from '../item-write/item-text-modal/item-text-modal.component';
import { mayCurate } from '../item-write/rules/item-write-writability.rules';

/**
 * The expanded listing view (HUBX-8481): the inline row's rules, store and form (it extends
 * ItemSimpleComponent, as it always has), plus what the inline row does not take - the XID,
 * the MPN, DQ #1-3, the attribute band, the ExW point, Restrictions and Special Notes.
 *
 * - The top band follows the Edit modal without copying it: Status under the logo; Part
 *   Number, MPN and XID stacked; then Description, Promo, Available / ETA, MOQ / MXQ, Cost
 *   with Price under it, and DQ #1-3. Description gives up width when the band is full.
 * - DQ #1-3 are always the three slots for ADMIN / VM, empty ones included, so a tier can be
 *   added here as in the modal. A Seller is never shown one: the form keeps the tiers
 *   disabled for it and its body carries none.
 *
 * - The attribute band is the popup's: Condition, Warranty and Packaging from
 *   /lookups/attributes, and nothing else. Free-form attributes (Visual Condition) have no
 *   control here either; they travel by echo, so a save leaves them in place (HUBX-8475).
 * - Special Notes is an inline field. The separate comment modal (app-item-comment, reached
 *   through the info button and the page's `openCommentModal`) is no longer reached from here.
 * - Restrictions is the v2 text member: "Yes" means there is text. Yes (or Edit) opens the
 *   popup's own text modal; No clears the text. Legacy's Restrictions attribute with its
 *   separate description modal (app-item-restriction) goes.
 * - The promo cluster draws the popup's three icons: Today's Deals from the item, Price Drop
 *   and Just Launched from the row envelope. Today's Deals is the modal's toggle when
 *   writableFields grants it (an operator); the other two are only shown. The fourth, "New", has had no writer since 2018
 *   (HUBX-2129).
 *
 * The legacy popup mode (`isPopup`, `onClose`) is gone with the block that used it: nothing
 * set `showDetailedPopUp` any more on either page that rendered it (HUBX-8388).
 */
@Component({
  selector: 'app-item-detailed',
  templateUrl: './item-detailed.component.html',
  styleUrls: ['./item-detailed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, NgFor, NgClass, FormsModule, ReactiveFormsModule, DropdownComponent, TooltipModule, OnlyDigitsDirective, NgxMaskModule, DatePipe, ItemTextModalComponent, CustomCurrencyPipe, CharCounterComponent]
})
export class ItemDetailedComponent extends ItemSimpleComponent {
  /** The restrictions text modal is open. */
  editingRestrictions = false;

  // Today's Deals: the Edit modal's toggle (HUBX-8387) when the server grants `todaysDeals`,
  // the plain icon otherwise. Like the modal, a click writes nothing on its own: it is the
  // row's edit, and the row's Save carries it - only for a caller granted it, since the body
  // omits the member otherwise.

  /** Granted by writableFields, and the control live: not on a locked or archived row. */
  get mayCurate(): boolean {
    const control = this.fg?.controls['todaysDeals'];
    return !!control?.enabled && mayCurate(this.baseline.writableFields ?? []);
  }

  get inTodaysDeals(): boolean {
    return !!this.fg?.controls['todaysDeals'].value;
  }

  toggleTodaysDeals(): void {
    if (!this.mayCurate) { return; }
    this.fg.controls['todaysDeals'].setValue(!this.inTodaysDeals);
    this.cdr.markForCheck();
  }

  get todaysDealsTooltip(): string {
    return this.inTodaysDeals ? 'Today\'s Deals \u2014 click to remove' : 'Today\'s Deals \u2014 click to add';
  }

  /** Over the simple row: the MPN, the attribute band, ExW and MXQ always, the XID for an
   *  operator. */
  protected override visibleControls(): string[] {
    return [
      ...super.visibleControls(), 'mpn', 'condition', 'warranty', 'packaging', 'exwId',
      ...(this.pricing ? ['hubxItemCode'] : [])
    ];
  }

  get hasRestrictions(): boolean {
    return !!String(this.fg?.controls['restrictions'].value ?? '').trim();
  }

  get restrictionsLocked(): boolean {
    return !!this.fg?.controls['restrictions'].disabled;
  }

  /** The same tooltip as the Special Notes chip's: named, then the text. */
  get restrictionsTooltip(): string {
    return 'Restrictions: ' + (this.fg?.controls['restrictions'].value ?? '');
  }

  /** The Yes / No select over the restrictions text. */
  onRestrictionsChoice(choice: string): void {
    if (this.restrictionsLocked) { return; }
    if (choice === 'Yes') {
      this.editingRestrictions = true;
    } else {
      this.fg.controls['restrictions'].setValue('');
    }
  }

  editRestrictions(): void {
    if (!this.restrictionsLocked) {
      this.editingRestrictions = true;
    }
  }

  onRestrictionsSaved(text: string): void {
    this.fg.controls['restrictions'].setValue(text ?? '');
    this.editingRestrictions = false;
    this.cdr.markForCheck();
  }

  /** Closed without saving: a "Yes" that got no text stays "No", as legacy forced it. */
  onRestrictionsClosed(): void {
    this.editingRestrictions = false;
    this.cdr.markForCheck();
  }

  // Special Notes: the Edit modal's chip, opening the same text modal. Nothing in this band
  // has a label outside its control, so the chip names itself - "Special Notes" while empty,
  // the note once there is one - and its tooltip always leads with the name.

  /** The special notes text modal is open. */
  editingNotes = false;

  get hasNotes(): boolean {
    return !!String(this.fg?.controls['specialNotes'].value ?? '').trim();
  }

  get notesLocked(): boolean {
    return !!this.fg?.controls['specialNotes'].disabled;
  }

  get notesText(): string {
    return this.hasNotes ? String(this.fg.controls['specialNotes'].value) : 'Special Notes';
  }

  get notesTooltip(): string {
    return this.hasNotes ? 'Special Notes: ' + this.fg.controls['specialNotes'].value : 'Special Notes';
  }

  editNotes(): void {
    if (!this.notesLocked) {
      this.editingNotes = true;
    }
  }

  onNotesSaved(text: string): void {
    this.fg.controls['specialNotes'].setValue(text ?? '');
    this.editingNotes = false;
    this.cdr.markForCheck();
  }

  onNotesClosed(): void {
    this.editingNotes = false;
    this.cdr.markForCheck();
  }
}
